import React from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import {
  UserCheck, Users, UserPlus, TrendingUp, TrendingDown,
} from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useDriverLocations } from '../hooks/useDriverLocations';
import { useDailyStats } from '../hooks/useDailyStats';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MAP_DEFAULT_CENTER: [number, number] = [59.3293, 18.0686];

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

const Dashboard: React.FC = () => {
  const { companyId, isCompanyAdmin } = useAuth();
  const scopedCompanyId = isCompanyAdmin ? companyId : null;
  const { stats, loading: statsLoading } = useDashboardStats(scopedCompanyId);
  const { locations, loading: mapLoading } = useDriverLocations(scopedCompanyId);
  const { data: dailyData, labels: dailyLabels, loading: dailyLoading } = useDailyStats(7, scopedCompanyId);

  const chartData = {
    labels: dailyLoading ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : dailyLabels,
    datasets: [
      {
        label: 'Total Trips',
        data: dailyLoading ? [0, 0, 0, 0, 0, 0, 0] : dailyData.map(d => d.total),
        backgroundColor: 'rgba(15,23,42,0.85)',
        borderRadius: 6,
      },
      {
        label: 'Completed',
        data: dailyLoading ? [0, 0, 0, 0, 0, 0, 0] : dailyData.map(d => d.completed),
        backgroundColor: 'rgba(6,182,212,0.85)',
        borderRadius: 6,
      },
      {
        label: 'Cancelled',
        data: dailyLoading ? [0, 0, 0, 0, 0, 0, 0] : dailyData.map(d => d.cancelled),
        backgroundColor: 'rgba(245,158,11,0.85)',
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 16, font: { size: 12 } } },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, border: { display: false } },
      x: { grid: { display: false }, border: { display: false } },
    },
  };

  const fmt = (n: number) => n.toLocaleString();
  const fmtMoney = (n: number) => `$ ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const driverStats = [
    { title: 'Active Drivers',         value: statsLoading ? null : (stats?.activeDrivers ?? 0),       icon: <UserCheck className="w-5 h-5" />, color: '#06B6D4' },
    { title: 'Offline Drivers',         value: statsLoading ? null : (stats?.offlineDrivers ?? 0),      icon: <Users className="w-5 h-5" />,     color: '#8B5CF6' },
    { title: 'New Drivers This Month',  value: statsLoading ? null : (stats?.newDriversThisMonth ?? 0), icon: <UserPlus className="w-5 h-5" />,  color: '#F59E0B' },
  ];

  const taxiStats = [
    { title: "Today's Income",     value: statsLoading ? null : fmtMoney(stats?.todayIncome ?? 0) },
    { title: 'This Week Income',   value: statsLoading ? null : fmtMoney(stats?.weekIncome ?? 0) },
    { title: 'This Month Income',  value: statsLoading ? null : fmtMoney(stats?.monthIncome ?? 0) },
  ];

  const tripStats = [
    { title: 'Booked Trips',   value: statsLoading ? null : fmt(stats?.bookedTrips ?? 0),           up: true,  gradient: 'linear-gradient(135deg,#06B6D4,#0891B2)' },
    { title: 'Cancelled Trips', value: statsLoading ? null : fmt(stats?.cancelledTrips ?? 0),       up: false, gradient: 'linear-gradient(135deg,#1e293b,#0f172a)' },
    { title: 'New Drivers',    value: statsLoading ? null : fmt(stats?.newDriversThisMonth ?? 0),   up: true,  gradient: 'linear-gradient(135deg,#F59E0B,#D97706)' },
    { title: 'Total Earning',  value: statsLoading ? null : fmtMoney(stats?.totalEarningThisMonth ?? 0), up: true, gradient: 'linear-gradient(135deg,#0891B2,#0e7490)' },
  ];

  const activeCount = locations.filter((l) => l.driver?.status === 'active').length;

  return (
    <Layout title="Dashboard" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Dashboard' }]}>
      <div className="space-y-6">

        {/* Live Map */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="live-dot" />
              <h2 className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                Live Location
              </h2>
            </div>
            <span className="text-xs text-slate-400">
              {mapLoading ? 'Loading…' : `${activeCount} driver${activeCount !== 1 ? 's' : ''} active`}
            </span>
          </div>
          <div className="relative z-0 h-80 overflow-hidden">
            <MapContainer
              center={MAP_DEFAULT_CENTER}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
              {locations.map((loc) => (
                <Marker key={loc.driver_id} position={[loc.lat, loc.lng]}>
                  <Popup>
                    <strong>{loc.driver?.full_name ?? 'Driver'}</strong>
                    <p className="text-xs text-gray-500 mt-0.5">Status: {loc.driver?.status ?? '—'}</p>
                    {loc.speed != null && <p className="text-xs text-gray-500">{loc.speed.toFixed(0)} km/h</p>}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Tachograph Stats */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Tachograph</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {driverStats.map((stat, i) => (
              <div key={i} className="card p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: `${stat.color}18`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div>
                  {stat.value === null ? <Skeleton className="h-8 w-16 mb-1" /> : (
                    <div className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>{stat.value}</div>
                  )}
                  <div className="text-sm text-slate-500">{stat.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Taxi Stats + Chart */}
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Taxi — Last 7 Days</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card p-6">
              <div className="space-y-5">
                {taxiStats.map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{s.title}</span>
                    {s.value === null ? <Skeleton className="h-5 w-20" /> : (
                      <span className="text-base font-bold text-slate-800">{s.value}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-around text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-800 inline-block" />Total</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-electric-500 inline-block" />Completed</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />Cancelled</span>
              </div>
            </div>
            <div className="card p-6 lg:col-span-2">
              {dailyLoading
                ? <Skeleton className="h-48 w-full" />
                : <Bar data={chartData} options={chartOptions} />
              }
            </div>
          </div>
        </div>

        {/* Trip Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tripStats.map((stat, i) => (
            <div key={i} className="rounded-2xl p-6 text-white relative overflow-hidden"
              style={{ background: stat.gradient, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-8 translate-x-8"
                style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div className="text-3xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                {stat.value === null ? <Skeleton className="h-8 w-20 bg-white/20" /> : stat.value}
              </div>
              <div className="text-white/80 text-sm mb-3">{stat.title}</div>
              <div className="flex items-center gap-1 text-xs font-semibold">
                {stat.up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                Live
              </div>
            </div>
          ))}
        </div>

      </div>
    </Layout>
  );
};

export default Dashboard;
